import type { LessonWorkspaceMode } from "../auth/api-client";

export interface CohortLessonStudentLinkInput {
  readonly cohortId: string;
  readonly lessonId: string;
  readonly lessonTitle: string;
  readonly iconName?: string | null;
  readonly iconBgColor?: string | null;
  readonly iconFgColor?: string | null;
  readonly workspaceMode?: LessonWorkspaceMode | null;
  /** When true, students can open a closed assignment in read-only review mode (in-app only). */
  readonly reviewClosed?: boolean;
}

export function buildCohortLessonStudentLink(
  input: CohortLessonStudentLinkInput,
  origin: string = typeof window !== "undefined" ? window.location.origin : ""
): string {
  const qp = new URLSearchParams({
    cohortId: input.cohortId,
    lessonId: input.lessonId,
    lessonTitle: input.lessonTitle,
  });
  if (input.iconName) qp.set("iconName", input.iconName);
  if (input.iconBgColor) qp.set("iconBgColor", input.iconBgColor);
  if (input.iconFgColor) qp.set("iconFgColor", input.iconFgColor);
  if (input.workspaceMode) qp.set("workspaceMode", input.workspaceMode);
  if (input.reviewClosed) qp.set("review", "1");
  return `${origin}/ide?${qp.toString()}`;
}

export function cohortLessonStudentLinkFromAssignment(
  cohortId: string,
  assignment: {
    itemKind?: "lesson" | "quiz";
    lessonId: string | null;
    quizId?: string | null;
    lessonTitle: string;
    iconName?: string | null;
    iconBgColor?: string | null;
    iconFgColor?: string | null;
    workspaceMode?: LessonWorkspaceMode | null;
  }
): string {
  if (assignment.itemKind === "quiz" && assignment.quizId) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/quiz?cohortId=${encodeURIComponent(cohortId)}&quizId=${encodeURIComponent(assignment.quizId)}`;
  }
  return buildCohortLessonStudentLink({
    cohortId,
    lessonId: assignment.lessonId ?? "",
    lessonTitle: assignment.lessonTitle,
    iconName: assignment.iconName,
    iconBgColor: assignment.iconBgColor,
    iconFgColor: assignment.iconFgColor,
    workspaceMode: assignment.workspaceMode,
  });
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
